import type { Message } from "@bufbuild/protobuf";
import type { AttachmentId, FullWeaponInfo, PerkId, WeaponBars } from "../../../api/types";

// Live stat preview for the loadout screen.
//
// UI APPROXIMATION ONLY. The catalog carries attachment and perk effects as text, so the
// multipliers below are transcribed by hand from docs/DESIGN.md ("Attachments", "Perks"); the
// server (`gr_core::weapons::resolve_stats`) is authoritative and may differ in detail (spread
// terms for movement/air/bloom, optic FOV, equip times and sound are not modelled here). Ids that
// are not in a table have no effect.

/** Multiplicative modifiers of one attachment or perk. A missing field means x1. */
export interface Mods {
  /** Both effective ranges. */
  range?: number;
  adsTime?: number;
  hipSpread?: number;
  adsSpread?: number;
  /** Movement and air spread terms. Not part of the static spread numbers; only feeds the accuracy bar. */
  moveSpread?: number;
  mag?: number;
  reload?: number;
  recoilV?: number;
  recoilH?: number;
  /** ADS movement speed. */
  adsMove?: number;
}

type FoldedMods = Required<Mods>;

const MOD_KEYS: readonly (keyof Mods)[] = [
  "range",
  "adsTime",
  "hipSpread",
  "adsSpread",
  "moveSpread",
  "mag",
  "reload",
  "recoilV",
  "recoilH",
  "adsMove",
];

/** docs/DESIGN.md "Attachments (max 3, one per slot)". Optic FOV, bloom, sound and flash are not modelled. */
export const ATTACHMENT_MODS: Readonly<Record<AttachmentId, Mods>> = {
  red_dot: {},
  holo: { adsTime: 1.03 },
  scope_2x: { adsSpread: 0.85, adsTime: 1.15 },
  scope_4x: { adsSpread: 0.7, adsTime: 1.25 },
  compensator: { recoilH: 0.75 },
  muzzle_brake: { recoilV: 0.8, hipSpread: 1.1 },
  suppressor: { range: 0.9 },
  long_barrel: { range: 1.25, adsTime: 1.08 },
  short_barrel: { range: 0.85, adsTime: 0.9, hipSpread: 0.9 },
  ext_mag: { mag: 1.5, reload: 1.12 },
  fast_mag: { reload: 0.75 },
  vertical_grip: { recoilV: 0.85, adsTime: 1.04 },
  angled_grip: { adsTime: 0.88 },
  laser: { hipSpread: 0.75 },
  light_stock: { adsMove: 1.08, recoilV: 1.08, recoilH: 1.08 },
};

/** docs/DESIGN.md "Perks (pick 2)": only the perks that touch weapon stats. */
export const PERK_MODS: Readonly<Record<PerkId, Mods>> = {
  quickdraw: { adsTime: 0.8 },
  steady_hands: { moveSpread: 0.65 },
  fast_hands: { reload: 0.8 },
};

export interface PreviewStats {
  dmgNear: number;
  dmgFar: number;
  rpm: number;
  mag: number;
  reload: number;
  adsTime: number;
  rangeNear: number;
  rangeFar: number;
  headMult: number;
  mobility: number;
  adsMobility: number;
  hipSpread: number;
  adsSpread: number;
}

export interface WeaponPreview {
  stats: PreviewStats;
  /** 0..100 */
  bars: BarValues;
}

/** The six bar values as a plain object: `WeaponBars` without the message brand (preview values are not wire integers). */
export type BarValues = Omit<WeaponBars, keyof Message>;

// Exponents that blend the three spread multipliers into one accuracy factor (sum = 1).
const HIP_WEIGHT = 0.35;
const ADS_WEIGHT = 0.45;
const MOVE_WEIGHT = 0.2;

function foldInto(acc: FoldedMods, ids: readonly string[], table: Readonly<Record<string, Mods>>): void {
  for (const id of new Set(ids)) {
    if (!Object.hasOwn(table, id)) continue;
    const mods = table[id];
    for (const key of MOD_KEYS) acc[key] *= mods[key] ?? 1;
  }
}

function clampBar(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * Weapon numbers and bars after the given attachments and perks. Pure; the input is not modified.
 * Bars: damage and fire rate stay as in the catalog, range follows the range multiplier, accuracy
 * the inverse of the blended spread multipliers, mobility the ADS move speed and control the
 * inverse of the geometric mean of the recoil multipliers.
 */
export function previewWeapon(
  weapon: FullWeaponInfo,
  attachments: readonly AttachmentId[],
  perks: readonly PerkId[],
): WeaponPreview {
  const m: FoldedMods = {
    range: 1,
    adsTime: 1,
    hipSpread: 1,
    adsSpread: 1,
    moveSpread: 1,
    mag: 1,
    reload: 1,
    recoilV: 1,
    recoilH: 1,
    adsMove: 1,
  };
  foldInto(m, attachments, ATTACHMENT_MODS);
  foldInto(m, perks, PERK_MODS);

  const base = weapon.bars;
  const spread = m.hipSpread ** HIP_WEIGHT * m.adsSpread ** ADS_WEIGHT * m.moveSpread ** MOVE_WEIGHT;
  const recoil = Math.sqrt(m.recoilV * m.recoilH);

  return {
    stats: {
      dmgNear: weapon.dmgNear,
      dmgFar: weapon.dmgFar,
      rpm: weapon.rpm,
      mag: Math.round(weapon.mag * m.mag),
      reload: weapon.reload * m.reload,
      adsTime: weapon.adsTime * m.adsTime,
      rangeNear: weapon.rangeNear * m.range,
      rangeFar: weapon.rangeFar * m.range,
      headMult: weapon.headMult,
      mobility: weapon.mobility,
      adsMobility: weapon.adsMobility * m.adsMove,
      hipSpread: weapon.hipSpread * m.hipSpread,
      adsSpread: weapon.adsSpread * m.adsSpread,
    },
    bars: {
      damage: clampBar(base.damage),
      fireRate: clampBar(base.fireRate),
      range: clampBar(base.range * m.range),
      accuracy: clampBar(base.accuracy / spread),
      mobility: clampBar(base.mobility * m.adsMove),
      control: clampBar(base.control / recoil),
    },
  };
}
